import axios from 'axios';
import API from "../config/API";

export const productService = {
    listProduct,
    addProduct,
    updateProduct,
    deleteProduct,
    getProduct,
    adjustStock,
    getProductsList,
    listProductCategories,
    listProductBrands,
    addProductCategory,
    addProductBrand,
    deleteProductCategory,
    updateProductCategoryOrder,
    updateProductOrder,
    updateProductBrand,
    deleteProductBrand,
    updateProductCategory,
    // Product commerce
    listOrders,
    getOrder,
    updateOrder,
    cancelOrder,
    createCheckout,
    getStore,
    updateStore,
    publishStore,
    unpublishStore,
    getProductReport,
    getPublicStore,
    getPublicProducts,
    getServiceReport
};

// ---- Product commerce ------------------------------------------------------
const _pcHeaders = () => ({
    'Content-Type': 'application/json',
    'authorization': "Basic YmV1YXBwOmJldWFwcA==",
    'accessToken': `${localStorage.getItem('accessToken')}`
});

// Normalise a backend product order to the shape the Orders UI consumes.
function mapOrder(o) {
    if (!o) return o;
    const fulfilled = ['packed', 'shipped', 'delivered', 'collected', 'ready_for_collection'].includes(o.fulfilmentStatus);
    const a = o.amounts || {};
    const d = o.delivery || {};
    return {
        id: o._id,
        number: o.orderNumber,
        createdAt: o.createdAt,
        channel: o.channel,
        customer: o.customer || {},
        delivery: {
            method: d.method === 'ship' ? 'ship' : 'collect',
            address: d.address || null,
            instructions: d.optionName || ''
        },
        items: (o.items || []).map((it) => ({
            productId: it.productId, name: it.name, qty: it.quantity,
            qtyFulfilled: fulfilled ? it.quantity : 0, unitPrice: it.unitPrice
        })),
        subtotal: a.subtotal || 0,
        shipping: a.shipping || 0,
        tax: 0,
        discount: 0,
        total: a.total || 0,
        paymentStatus: o.paymentStatus,
        fulfilmentStatus: o.fulfilmentStatus,
        tracking: (d.tracking && (d.tracking.number || d.tracking.courier)) ? d.tracking : null,
        customerNote: o.customerNote || '',
        internalNote: o.internalNote || '',
        payout: o.payout || null,
        timeline: (o.timeline || []).map((t) => ({ at: t.at, label: t.note || t.status }))
    };
}

function _unwrap(res) {
    return res && res.data ? (res.data.responseData !== undefined ? res.data.responseData : res.data) : {};
}

// Salon-side order list -> mapped array.
function listOrders(filters) {
    return axios.get(API.PRODUCT_ORDERS, { headers: _pcHeaders(), params: Object.assign({ _ts: Date.now() }, filters || {}) })
        .then((res) => {
            const d = _unwrap(res);
            const list = Array.isArray(d) ? d : (d.orders || []);
            return list.map(mapOrder);
        });
}

function getOrder(orderId) {
    return axios.get(`${API.PRODUCT_ORDERS}/${orderId}`, { headers: _pcHeaders() })
        .then((res) => mapOrder(_unwrap(res)));
}

// Update fulfilment status / tracking / internal note.
function updateOrder(orderId, patch) {
    return axios.put(`${API.PRODUCT_ORDERS}/${orderId}`, patch || {}, { headers: _pcHeaders() })
        .then((res) => mapOrder(_unwrap(res)));
}

function cancelOrder(orderId, reason) {
    // BE returns { order, refunded, reversed, refundErrors } — the money
    // outcome rides along so the UI can report what was refunded.
    return axios.post(`${API.PRODUCT_ORDERS}/${orderId}/cancel`, { reason: reason || '' }, { headers: _pcHeaders() })
        .then((res) => {
            const d = _unwrap(res) || {};
            return {
                order: mapOrder(d.order || d),
                refunded: parseFloat(d.refunded) || 0,
                reversed: parseFloat(d.reversed) || 0,
                refundErrors: d.refundErrors || null,
            };
        });
}

// Customer storefront checkout -> { url, sessionId, orderId }.
function createCheckout(payload) {
    return axios.post(API.PRODUCT_CHECKOUT, payload || {}, { headers: _pcHeaders() });
}

function getStore() {
    return axios.get(API.PRODUCT_STORE, { headers: _pcHeaders(), params: { _ts: Date.now() } });
}

// Public storefront read of a salon's store (published settings + delivery options).
function getPublicStore(salonId) {
    return axios.get(`${API.PRODUCT_STORE}/public/${salonId}`, { headers: _pcHeaders(), params: { _ts: Date.now() } })
        .then((res) => (res && res.data ? (res.data.responseData !== undefined ? res.data.responseData : res.data) : {}));
}

// Public storefront catalogue (no user token) — the salon's online products,
// customer-safe fields only. { published, products: [] } unless the store is live.
function getPublicProducts(salonId) {
    return axios.get(`${API.PRODUCT_STORE}/public/${salonId}/products`, { headers: _pcHeaders(), params: { _ts: Date.now() } })
        .then((res) => (res && res.data ? (res.data.responseData !== undefined ? res.data.responseData : res.data) : {}));
}

function updateStore(body) {
    return axios.put(API.PRODUCT_STORE, body || {}, { headers: _pcHeaders() });
}

function publishStore() {
    return axios.post(`${API.PRODUCT_STORE}/publish`, {}, { headers: _pcHeaders() });
}

function unpublishStore() {
    return axios.post(`${API.PRODUCT_STORE}/unpublish`, {}, { headers: _pcHeaders() });
}

function getProductReport(filters) {
    return axios.get(API.PRODUCT_REPORTING, { headers: _pcHeaders(), params: filters || {} });
}

function getServiceReport(filters) {
    return axios.get(API.SERVICE_REPORTING, { headers: _pcHeaders(), params: filters || {} });
}

function listProduct(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
      'Content-Type': 'application/json',
      'authorization': "Basic YmV1YXBwOmJldWFwcA==",
      'accessToken': `${accessToken}`
    };
  
    // Always pass all parameters to ensure filters work correctly.
    // `_ts` is a cache-buster: without a unique URL the browser can serve a
    // stale cached response after a save (e.g. a newly added product image
    // wouldn't appear until a hard refresh).
    return axios.get(API.LIST_PRODUCT, {
      headers: headers,
      params: {
        search: params.search || undefined,
        page: params.page,
        limit: params.limit,
        stockStatus: params.stockStatus || undefined,
        categoryId: params.categoryId || undefined,
        brandId: params.brandId || undefined,
        _ts: Date.now()
      }
    });
  }

  function getProductsList(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
      'Content-Type': 'application/json',
      'authorization': "Basic YmV1YXBwOmJldWFwcA==",
      'accessToken': `${accessToken}`
    };
  
    if (params?.salonId!=='') {
      return axios.get(API.GET_SALON_PRODUCT, {
        headers: headers,
        params: {
          salonId: params.salonId,
          search: params.search || undefined,
          stockStatus: params.stockStatus || undefined,
          categoryId: params.categoryId || undefined,
          brandId: params.brandId || undefined,
          _ts: Date.now() // cache-buster so fresh products/images show without a hard refresh
        }
      });
    }
  }

  
  

function getProduct(params) {
    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    // console.log("headers",headers, params);
    return axios.get(API.GET_PRODUCT,{headers:headers})
}

// Change stock by `change` (+/−) with a reason; the server records it in the
// stock history. { productId, variantId?, change, reason, note? } → { quantity }
function adjustStock(body) {
    return axios.post(API.PRODUCT_STOCK_ADJUST, { ...body, salonId: localStorage.getItem('salonId') }, { headers: _pcHeaders() })
        .then((res) => {
            const d = res && res.data;
            if (!d || d.statusCode !== 1) {
                const err = new Error((d && d.error && d.error.responseMessage) || 'Stock not changed');
                err.response = res;
                throw err;
            }
            return d.responseData && (d.responseData.result || d.responseData);
        });
}

function addProduct(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // If params is FormData, don't set Content-Type
    // Axios will automatically set it to 'multipart/form-data'
    if (!(params instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }
    
    console.log("Adding product with params:", params);
    return axios.post(API.ADD_PRODUCT, params, {headers: headers})
}

function listProductCategories() {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.get(API.LIST_PRODUCT_CATEGORIES, {
        headers: headers
    });
}

function listProductBrands() {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.get(API.LIST_PRODUCT_BRANDS, {
        headers: headers
    });
}
function updateProduct(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    // If params is FormData, don't set Content-Type
    // Axios will automatically set it to 'multipart/form-data'
    if (!(params instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }
    
    console.log("Updating product with params:", params);
    return axios.put(API.UPDATE_PRODUCT, params, {headers: headers})
}

function deleteProduct(params) {
    let accessToken = localStorage.getItem('accessToken');
    let  headers= {
        'Content-Type' : 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    // console.log("headers",headers, params);
    return axios.post(API.DELETE_PRODUCT, params, {headers:headers})
}

function addProductCategory(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.ADD_PRODUCT_CATEGORY, params, {
        headers: headers
    });
}

function updateProductCategoryOrder(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    console.log("Updating product category order with params:", params);
    return axios.post(API.PRODUCT_CATEGORY_ORDERCHANGE, params, {
        headers: headers
    });
}

function updateProductOrder(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    console.log("Updating product order with params:", params);
    return axios.post(API.PRODUCT_ORDERCHANGE, params, {
        headers: headers
    });
}

function addProductBrand(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    return axios.post(API.ADD_PRODUCT_BRAND, params, {
        headers: headers
    });
}

function deleteProductCategory(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    console.log("Deleting product category with params:", params);
    return axios.post(API.DELETE_PRODUCT_CATEGORY, params, {
        headers: headers
    });
}

function updateProductCategory(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    console.log("Updating product category with params:", params);
    return axios.put(API.UPDATE_PRODUCT_CATEGORY, params, {
        headers: headers
    });
}

function updateProductBrand(params) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    console.log("Updating product brand with params:", params);
    return axios.put(API.UPDATE_PRODUCT_BRAND, params, {
        headers: headers
    });
}

function deleteProductBrand(brandId) {
    let accessToken = localStorage.getItem('accessToken');
    let headers = {
        'Content-Type': 'application/json',
        'authorization': "Basic YmV1YXBwOmJldWFwcA==",
        'accessToken': `${accessToken}`
    };
    
    console.log("Deleting product brand with ID:", brandId);
    // Make sure brandId is passed correctly to the API
    const payload = typeof brandId === 'object' ? brandId : { brandId };
    return axios.post(API.DELETE_PRODUCT_BRAND, payload, {
        headers: headers
    });
}

